<script setup lang="ts">
import { computed, watch } from 'vue';

import { PHONE_COUNTRY_OPTIONS, type PhoneCountryOption } from '@peanuds/shared';
import LockIcon from '../../assets/icons/lock.svg?component';

interface Props {
  form?: Record<
    string,
    { touched?: boolean; invalid?: boolean; error?: { message?: string } } | undefined
  > | null;
  label: string;
  numberId?: string;
  countryName?: string;
  numberName?: string;
  disabled?: boolean;
  autoSelectFirst?: boolean;
  options?: ReadonlyArray<PhoneCountryOption>;
}

const props = withDefaults(defineProps<Props>(), {
  numberId: 'user-phone-number',
  countryName: 'phoneCountry',
  numberName: 'phoneNumber',
  disabled: false,
  autoSelectFirst: true,
});

const phoneCountries = computed(() => props.options ?? PHONE_COUNTRY_OPTIONS);
const countryModel = defineModel<PhoneCountryOption | null>('country', { default: null });
const numberModel = defineModel<string>('number', { default: '' });

const resolvePhoneCountry = (country: PhoneCountryOption | null): PhoneCountryOption | null => {
  if (!country) {
    return null;
  }

  return (
    phoneCountries.value.find((candidate) => candidate.iso2 === country.iso2) ??
    phoneCountries.value.find((candidate) => candidate.phone === country.phone) ??
    null
  );
};

function handlePhoneNumberInput(nextValue: string | undefined) {
  numberModel.value = (nextValue ?? '').replace(/[^\d]/g, '').slice(0, 14);
}

watch(
  [countryModel, phoneCountries],
  ([nextCountry]) => {
    const resolvedCountry = resolvePhoneCountry(nextCountry);

    if (resolvedCountry !== nextCountry) {
      countryModel.value = resolvedCountry;
      return;
    }

    if (!resolvedCountry && props.autoSelectFirst) {
      countryModel.value = phoneCountries.value[0] ?? null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="ui-phone-number-field">
    <label class="ui-phone-number-field__label" :for="numberId">{{ label }}</label>

    <InputGroup class="ui-phone-number-field__inputgroup">
      <div
        class="ui-phone-number-field__country-wrapper"
        :class="{ 'ui-phone-number-field__country-wrapper--disabled': disabled }"
      >
        <AppSelect
          v-model="countryModel"
          :name="countryName"
          :options="phoneCountries"
          optionLabel="name"
          dataKey="iso2"
          class="ui-phone-number-field__country-select"
          :panelStyle="{ width: '16rem' }"
          :disabled="disabled"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="ui-phone-number-field__country-item">
              <span class="ui-phone-number-field__country-flag" aria-hidden="true">
                {{ slotProps.value.flag }}
              </span>
              <span class="ui-phone-number-field__country-phone">
                {{ slotProps.value.phone }}
              </span>
            </div>
            <span v-else>--</span>
          </template>
          <template #option="slotProps">
            <div class="ui-phone-number-field__country-item">
              <span class="ui-phone-number-field__country-flag" aria-hidden="true">
                {{ slotProps.option.flag }}
              </span>
              <div class="ui-phone-number-field__country-option-text">
                <span>{{ slotProps.option.name }}</span>
                <span class="ui-phone-number-field__country-option-phone">
                  {{ slotProps.option.phone }}
                </span>
              </div>
            </div>
          </template>
        </AppSelect>
      </div>

      <IconField class="ui-phone-number-field__number-field">
        <InputText
          :id="numberId"
          type="number"
          :modelValue="numberModel"
          @update:modelValue="handlePhoneNumberInput"
          :name="numberName"
          class="ui-phone-number-field__input"
          placeholder="--"
          inputmode="numeric"
          autocomplete="tel-national"
          maxlength="14"
          :disabled="disabled"
        />
        <InputIcon v-if="props.disabled">
          <LockIcon class="p-button-icon-right" />
        </InputIcon>
      </IconField>
    </InputGroup>

    <small
      v-if="!!form?.[countryName]?.touched && !!form?.[countryName]?.invalid"
      class="ui-phone-number-field__error"
    >
      {{ form?.[countryName]?.error?.message }}
    </small>
    <small
      v-if="!!form?.[numberName]?.touched && !!form?.[numberName]?.invalid"
      class="ui-phone-number-field__error"
    >
      {{ form?.[numberName]?.error?.message }}
    </small>
  </div>
</template>

<style scoped lang="scss">
.ui-phone-number-field {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &__label {
    padding-left: 0.75rem;
    font-size: 1rem;
    font-weight: $font-semibold;
    color: var(--p-text-color);
  }

  &__inputgroup {
    display: flex;
    gap: 0.75rem;
    align-items: stretch;
  }

  &__country-wrapper--disabled {
    cursor: not-allowed;
  }

  &__country-select {
    width: auto;
    height: 100%;
    border-radius: 0.75rem;
    background: var(--p-surface-200);
    border-color: var(--p-form-field-border-color);
    box-shadow: 0 1px 2px 0 rgba(18, 18, 23, 0.05);

    &.p-select {
      height: 3rem;
    }

    &.p-disabled {
      /* PrimeVue dims disabled select; Figma shows full-opacity disabled inputs */
      opacity: 1;
    }

    /* Figma doesn't show a caret */
    :deep(.p-select-dropdown) {
      display: none;
    }

    :deep(.p-select-label) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__number-field {
    width: 100%;
  }

  &__input {
    width: 100%;
    height: 3rem;
    border-radius: 0.75rem;
    box-shadow: 0 1px 2px 0 rgba(18, 18, 23, 0.05);
    appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      margin: 0;
      appearance: none;
    }

    &:disabled {
      background-color: var(--p-surface-200);
      border-color: var(--p-form-field-border-color);
      color: var(--p-surface-500);
      font-weight: $font-medium;
      cursor: not-allowed;
    }
  }

  &__country-item {
    display: flex;
    align-items: center;
    gap: 0.765625rem; /* 12.25px */
    line-height: 1;
  }

  &__country-flag {
    font-size: 1rem;
    line-height: 1;
  }

  &__country-phone {
    font-size: 1rem;
    font-weight: $font-medium;
    color: var(--p-text-color);
  }

  /* Disabled country prefix should also be surface/500 */
  &__country-wrapper--disabled &__country-phone {
    color: var(--p-surface-500);
  }

  &__country-option-text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__country-option-phone {
    color: var(--p-surface-500);
    font-size: 0.875rem;
  }

  &__error {
    color: var(--p-red-500, #ef4444);
    font-weight: var(--p-message-text-font-weight);
    font-size: var(--p-message-text-sm-font-size);
  }
}
</style>
